import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    Inject,
    LOCALE_ID,
} from '@angular/core';
import { FilterBase } from '../filter-base';
import { Filter } from '../filter';
import { GlobalFilterColor, GlobalFilterProperty } from '@klickdata/core/table';
import { CustomField, FieldType } from '@klickdata/core/custom-field';
import { AuthService } from '@klickdata/core/auth';
import { debounceTime, filter, first, map, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { CustomerService } from '@klickdata/core/customer';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import * as moment from 'moment';
import { Utils } from '@klickdata/core/util';
import { AppScope } from '@klickdata/core/resource';

@Component({
    selector: 'app-custom-fields-filter',
    templateUrl: './custom-fields-filter.component.html',
    styleUrls: ['./custom-fields-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: FilterBase, useExisting: CustomFieldsFilterComponent }],
})
export class CustomFieldsFilterComponent extends FilterBase implements OnInit, OnDestroy {
    @ViewChild(MatSelect, { static: false }) matSelect: MatSelect;
    @Input() label: string;
    @Input() inGlobalFilter: boolean;
    @Input() scope: AppScope;
    @Output() filterChange: EventEmitter<Filter<string>> = new EventEmitter<Filter<string>>();
    @Output() filterRemoval: EventEmitter<Filter<string>> = new EventEmitter<Filter<string>>();
    protected _filter: Filter<string> = {
        property: GlobalFilterProperty.CUSTOM_FIELD,
        items: [],
        label: $localize`:@@customField:Custom field`,
        icon: 'tune',
        color: { bg: GlobalFilterColor.C2, fg: GlobalFilterColor.WHITE },
        styleClass: 'global-groups-filter-selector',
    };

    public customFields: CustomField[];
    private destroy: Subject<boolean> = new Subject<boolean>();
    FieldType = FieldType;
    public customFieldForm: FormGroup;
    Utils = Utils;
    constructor(
        protected auth: AuthService,
        protected fb: FormBuilder,
        protected cdr: ChangeDetectorRef,
        @Inject(LOCALE_ID) public localeId: string
    ) {
        super();
        this.customFieldForm = this.fb.group({
            id: [''],
            value: [''],
            name: [''],
            placeholder: [''],
            options: [''],
            type: [''],
        });
    }

    ngOnInit(): void {
        this.auth
            .getCustomer()
            .pipe(map((customer) => customer.custom_fields))
            .subscribe((customFields) => {
                this.customFields = customFields.filter((cf) => (this.scope ? cf.scope_id === this.scope : true));
                this.cdr.markForCheck();
            });
        this.customFieldForm
            .get('value')
            .valueChanges.pipe(
                debounceTime(300),
                takeUntil(this.destroy),
                filter((value) => value != null)
            )
            .subscribe((value) => {
                // FE sends items > fieldId|value1;value2;value3
                this.filter.items = [`${this.customFieldForm.value.id}|${this.getFilterValue(value)}`];
                this.filterChange.emit(this._filter);
            });
    }
    getFilterChip(filter: Filter<any>): { icon: string; tooltip: string; name: any; value: any } {
        let id: number, value: any;
        if (filter.items[0].includes('|')) {
            [id, value] = filter.items[0].split('|');
        } else if (filter.items[0].includes(';')) {
            [id, value] = filter.items[0].split(';');
        } else {
            [id, value] = [filter.items[0], ''];
        }
        const typeInfo = this.getCustomFieldTypeInfo(this.customFields.find((cf) => cf.id == id)?.type);
        if (id && typeInfo) {
            return {
                icon: typeInfo.icon,
                tooltip: typeInfo.label,
                name: this.customFields.find((cf) => cf.id == id)?.name,
                value: this.getFilterChipValue(id, value),
            };
        }
    }
    private getFilterChipValue(id: any, value: any) {
        switch (this.customFields.find((cf) => cf.id == id)?.type) {
            case FieldType.DATE:
                return new Date(value).toLocaleDateString('en-GB');
            case FieldType.MULTIPLE_CHOICE:
                const customFOptions = this.customFields.find((cf) => cf.id == id)?.options;
                const translationArray = customFOptions
                    .filter((opt) => value.split(';').map(Number).includes(opt.id))
                    .map((opt) => opt.value);
                const returnedValue = [];
                translationArray.forEach((trans) =>
                    returnedValue.push(Utils.getValueFromTranslation(trans, this.localeId))
                );
                return returnedValue.join(' ,');
            case FieldType.SINGLE_CHOICE:
            case FieldType.DROPDOWN:
                const valueTranslated = this.customFields
                    .find((cf) => cf.id == id)
                    ?.options.find((opt) => opt.id == value).value;
                return Utils.getValueFromTranslation(valueTranslated, this.localeId);
            default:
                return value;
        }
    }
    private getFilterValue(value: any) {
        switch (this.customFieldForm.value.type) {
            case FieldType.MULTIPLE_CHOICE:
                return value.map((v) => v.id).join(';');
            default:
                return value;
        }
    }
    getCustomFieldTypeInfo(type: string) {
        switch (type) {
            case FieldType.TEXT_INPUT:
                return { icon: 'notes', label: $localize`Text content` };
            case FieldType.SINGLE_CHOICE:
                return { icon: 'radio_button_checked', label: $localize`Single choice` };
            case FieldType.MULTIPLE_CHOICE:
                return { icon: 'check_box', label: $localize`Multiple choice` };
            case FieldType.DATE:
                return { icon: 'calendar_month', label: $localize`Date picker` };
            case FieldType.DROPDOWN:
                return { icon: 'format_list_numbered', label: $localize`Drop down` };
            case FieldType.SWITCH:
                return { icon: 'toggle_off', label: $localize`Switch` };
        }
    }
    onSelectionChange(field: CustomField) {
        if (field) {
            this.matSelect.value = field;
            this.customFieldForm.patchValue(field);
            this.cdr.markForCheck();
        }
    }
    public clear(): void {
        this._filter.items = null;
        this.customFieldForm.reset({ emitEvent: false });
        this.matSelect.value = undefined;
        this.filterRemoval.emit(this.filter);
        this.cdr.markForCheck();
    }
    public get filter(): Filter<string> {
        return this._filter;
    }
    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
